import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import Restricted from 'src/app/permissions/restricted';
import { MenuInnerWithSub } from './MenuInnerWithSub';
import { MenuItem } from './MenuItem';
import { ReportsMegaMenu } from './ReportsMegaMenu';
import { useAppSelector } from 'src/services/redux/app/store';
import { DynamicMenu } from './DynamicMenu';
import { useMyMenu } from './useMyMenu';

// Yetki kontrolü için yardımcı fonksiyon
const hasPermission = (authKeys: string[] | undefined, permission: string): boolean => {
  if (!authKeys) return false;
  return authKeys.includes(permission);
};

// Birden fazla yetkiden herhangi birine sahip mi kontrolü
const hasAnyPermission = (authKeys: string[] | undefined, permissions: string[]): boolean => {
  if (!authKeys) return false;
  return permissions.some((p) => authKeys.includes(p));
};

// Üst menü Menü Yönetimi'nden (/admin/menu-management) gelir. Aşağıdaki sabit
// menü yalnızca YEDEKTİR: istek başarısız olursa (ör. backend henüz
// güncellenmemiş) ya da firma için menü hiç tanımlanmamışsa kullanılır.
// Menüye madde eklemek için burayı değil yönetim sayfasını kullanın.
export function MenuInner() {
  const { data, isError } = useMyMenu();

  if (isError || (data && !data.configured)) return <StaticMenuInner />;
  if (!data) return null;
  return <DynamicMenu tree={data.tree} />;
}

function StaticMenuInner() {
  const intl = useIntl();
  const { user } = useAppSelector((state) => state.auth);
  const authKeys = user?.authorization_keys;

  // Alt menü görünürlük kontrolü
  const showCustomerMenu = useMemo(
    () => hasAnyPermission(authKeys, ['ROLE_CRM_CUSTOMERS', 'ROLE_CRM_CUSTOMER_VISITS', 'ROLE_CRM_CUSTOMER_FIRST_INVOICE', 'ROLE_CRM_CUSTOMER_NEWTOMAC', 'ROLE_CRM_QUOTES']),
    [authKeys],
  );

  const showProductMenu = useMemo(
    () => hasAnyPermission(authKeys, ['ROLE_CRM_PRODUCT_PRODUCTSONTHEWAY', 'ROLE_CRM_PRODUCT_BRANDWORKS', 'ROLE_CRM_SALES_OPPORTUNITIES', 'ROLE_CRM_PIPELINE']),
    [authKeys],
  );

  const showRebateMenu = useMemo(() => hasAnyPermission(authKeys, ['ROLE_CRM_REBATE_MAJORS', 'ROLE_CRM_REBATE_MAJORS_RATES', 'ROLE_CRM_REBATE_DISCOUNTS']), [authKeys]);

  const showMarketingMenu = useMemo(() => hasPermission(authKeys, 'ROLE_ADMIN'), [authKeys]);

  const showMailingMenu = useMemo(() => hasAnyPermission(authKeys, ['ROLE_CRM_MAILING', 'ROLE_ADMIN']), [authKeys]);

  const showPurchaseRequestsItem = useMemo(() => hasPermission(authKeys, 'ROLE_PURCHASE_REQUESTS'), [authKeys]);

  const showCRMMenu = useMemo(
    () => showCustomerMenu || showProductMenu || showRebateMenu || showMarketingMenu || showMailingMenu || showPurchaseRequestsItem,
    [showCustomerMenu, showProductMenu, showRebateMenu, showMarketingMenu, showMailingMenu, showPurchaseRequestsItem],
  );

  const showReportsMenu = useMemo(
    () => hasAnyPermission(authKeys, ['ROLE_REPORT_STOCK', 'ROLE_REPORT_SALES', 'ROLE_REPORT_SALESS', 'ROLE_REPORT_AGED_STOCK', 'ROLE_ADMIN']),
    [authKeys],
  );

  // Tedarik menüsü — koşul, İÇİNDEKİ maddelerin yetkisiyle BİREBİR aynı olmalı.
  // Menüdeki üç madde de ROLE_PURCHASING istiyor; başlığı ROLE_ADMIN veya
  // ROLE_PURCHASING_ADMIN'e de açmak, o kullanıcıya BOŞ bir "Tedarik" başlığı
  // gösteriyordu (mail günlüğü /admin'e taşınınca ortaya çıktı).
  const showPurchasingMenu = useMemo(() => hasPermission(authKeys, 'ROLE_PURCHASING'), [authKeys]);

  const showEcommerceMenu = user?.is_admin;

  return (
    <>
      {/* <Restricted to="ROLE_DASHBOARD_WIDGET1">
        <MenuItem title={'Anasayfa'} to="/dashboard" />
      </Restricted> */}

      {showCRMMenu && (
        <MenuInnerWithSub title="CRM" to="/apps" menuPlacement="bottom-start" menuTrigger="click">
          {showCustomerMenu && (
            <MenuInnerWithSub
              title="Müşteri İşlemleri"
              to="/apps/chat"
              icon="/media/icons/duotone/Communication/Group.svg"
              hasArrow={true}
              menuPlacement="right-start"
              menuTrigger={`{default:'click', lg: 'hover'}`}
            >
              <Restricted to="ROLE_CRM_CUSTOMERS">
                <MenuItem to="/customers" title="Müşterilerim" hasBullet={true} />
              </Restricted>
              <Restricted to="ROLE_CRM_CUSTOMERS || ROLE_CRM_CUSTOMER_VISITS || ROLE_CRM_CUSTOMER_FIRST_INVOICE || ROLE_CRM_CUSTOMER_NEWTOMAC">
                <MenuItem to="/customer-interactions" title="Müşteri Etkileşimleri" hasBullet={true} />
              </Restricted>
              <Restricted to="ROLE_CRM_CUSTOMERS">
                <MenuItem to="/my-potential-customers" title="Potansiyel Müşteri Takibi" hasBullet={true} />
              </Restricted>
              <Restricted to="ROLE_CRM_CUSTOMERS">
                <MenuItem to="/my-dormant-customers" title="Gerçek Müşteri Takibi" hasBullet={true} />
              </Restricted>
              <Restricted to="ROLE_CRM_CUSTOMERS">
                <MenuItem to="/my-churn-risks" title="Soğuyan Müşteriler" hasBullet={true} />
              </Restricted>
            </MenuInnerWithSub>
          )}

          <Restricted to="ROLE_CRM_QUOTES">
            <MenuItem to="/quotes" title="Tekliflerim" icon="/media/icons/duotone/Shopping/Price1.svg" />
          </Restricted>

          {/* Satışçının kendi (ve ekibinin) tedarik talepleri — satın alma yetkisi GEREKMEZ */}
          <Restricted to="ROLE_PURCHASE_REQUESTS">
            <MenuItem
              to="/my-purchase-requests"
              title="Tedarik Taleplerim"
              icon="/media/icons/duotone/Communication/Mail-attachment.svg"
            />
          </Restricted>

          {showMailingMenu && (
            <Restricted to="ROLE_CRM_MAILING || ROLE_ADMIN">
              <MenuItem to="/mailing" title="Mailing Pazarlama" icon="/media/icons/duotone/Communication/Outgoing-box.svg" />
            </Restricted>
          )}

          {showProductMenu && (
            <MenuInnerWithSub
              title="Ürün İşlemleri"
              to="/apps/chat"
              icon="/media/icons/duotone/Interface/Doughnut.svg"
              hasArrow={true}
              menuPlacement="right-start"
              menuTrigger={`{default:'click', lg: 'hover'}`}
            >
              <Restricted to="ROLE_CRM_PRODUCT_PRODUCTSONTHEWAY">
                <MenuItem to="/products-on-the-way" title="Yoldaki Ürünler" hasBullet={true} />
              </Restricted>
              <Restricted to="ROLE_CRM_PRODUCT_BRANDWORKS">
                <MenuItem to="/brand-works" title="Proje Satışları" hasBullet={true} />
              </Restricted>
              <Restricted to="ROLE_CRM_PIPELINE">
                <MenuItem to="/pipeline-records" title="Pipeline Kayıtları" hasBullet={true} />
              </Restricted>
              <Restricted to="ROLE_CRM_PIPELINE">
                <MenuItem to="/pipeline-report" title="Pipeline Raporu" hasBullet={true} />
              </Restricted>
              <Restricted to="ROLE_CRM_PRODUCT_BRANDWORKS">
                <MenuItem to="/hp-conversions" title="HP Dönüşümleri" hasBullet={true} />
              </Restricted>
            </MenuInnerWithSub>
          )}

          {showRebateMenu && (
            <MenuInnerWithSub
              title="Rebate İşlemleri"
              to="/apps/chat"
              icon="/media/icons/duotone/Shopping/Sale1.svg"
              hasArrow={true}
              menuPlacement="right-start"
              menuTrigger={`{default:'click', lg: 'hover'}`}
            >
              <Restricted to="ROLE_CRM_REBATE_MAJORS">
                <MenuItem to="/crt-customers" title="Major Müşteriler" hasBullet={true} />
              </Restricted>
              <Restricted to="ROLE_CRM_REBATE_MAJORS_RATES">
                <MenuItem to="/crt-discount-rates" title="Major İndirim Oranları" hasBullet={true} />
              </Restricted>
              <Restricted to="ROLE_CRM_REBATE_DISCOUNTS">
                <MenuItem to="/rebate-discounts" title="İndirim Oranları" hasBullet={true} />
              </Restricted>
            </MenuInnerWithSub>
          )}

          {showMarketingMenu && (
            <MenuInnerWithSub
              title="Marketing"
              to="/apps/marketing"
              icon="/media/icons/duotone/Communication/Outgoing-box.svg"
              hasArrow={true}
              menuPlacement="right-start"
              menuTrigger={`{default:'click', lg: 'hover'}`}
            >
              <Restricted to="ROLE_ADMIN">
                <MenuItem to="/marketing-customers" title="Marketing Müşterileri" hasBullet={true} />
              </Restricted>
              <Restricted to="ROLE_ADMIN">
                <MenuItem to="/customer-distribution" title="Müşteri Dağıtım Yönetimi" hasBullet={true} />
              </Restricted>
              <Restricted to="ROLE_ADMIN">
                <MenuItem to="/lead-scanning" title="Aday Taraması ve AI Sınıflandırma" hasBullet={true} />
              </Restricted>
              <Restricted to="ROLE_ADMIN">
                <MenuItem to="/lead-scanning-analytics" title="Aday Taraması Analiz Raporu" hasBullet={true} />
              </Restricted>
              <Restricted to="ROLE_ADMIN">
                <MenuItem to="/customer-surveys" title="5 Şubat - Müşteri Anketleri" hasBullet={true} />
              </Restricted>
            </MenuInnerWithSub>
          )}

          <MenuItem to="/documents" title="Medya İçerikleri" icon="/media/icons/duotone/Files/Group-folders.svg" />
        </MenuInnerWithSub>
      )}

      {showReportsMenu && (
        <MenuInnerWithSub isMega={true} megaWidthClass="w-lg-800px" title="Raporlar" to="/reports" menuPlacement="bottom-start" menuTrigger="click">
          <ReportsMegaMenu />
        </MenuInnerWithSub>
      )}

      {showEcommerceMenu && (
        <Restricted to="ROLE_ECOMMERCE">
          <MenuInnerWithSub title="E-Ticaret" to="/ecommerce" menuPlacement="bottom-start" menuTrigger="click">
            <MenuItem to="/product-comparison" title="Ürün Karşılaştırma Sistemi" hasBullet={true} />
            <MenuItem to="/ticimax-orders" title="Ticimax Siparişleri" hasBullet={true} />
          </MenuInnerWithSub>
        </Restricted>
      )}
      <Restricted to="ROLE_DEPCUSTOMERS">
        <MenuInnerWithSub title="DEP" to="/dep/enrollments" menuPlacement="bottom-start" menuTrigger="click">
          <MenuItem to="/dep/enrollments" title="Enrollments" hasBullet={true} />
          <MenuItem to="/dep/devices" title="Devices" hasBullet={true} />
          <MenuItem to="/dep/customers" title="Customers" hasBullet={true} />
        </MenuInnerWithSub>
      </Restricted>

      {/* Depo — yalnızca ROLE_DEPO yetkisi olanlara görünür */}
      <Restricted to="ROLE_DEPO">
        <MenuInnerWithSub title="Depo" to="/kargo-irsaliye" menuPlacement="bottom-start" menuTrigger="click">
          <MenuItem to="/kargo-irsaliye" title="Kargo İrsaliyeleri" hasBullet={true} />
          <MenuItem to="/kargo" title="UPS Kargo Yönetimi" hasBullet={true} />
        </MenuInnerWithSub>
      </Restricted>

      {/* Stok Alarmları — yalnızca ROLE_STOCK_ALARM yetkisi olanlara görünür */}
      <Restricted to="ROLE_STOCK_ALARM">
        <MenuItem to="/stock-alarms" title="Stok Alarmları" />
      </Restricted>
      {/* Apple Sektör Programı (FY27) */}
      <Restricted to="ROLE_APPLE_PROGRAM">
        <MenuItem to="/apple-program" title="Sektör Programı" />
      </Restricted>
      {/* İnsan Kaynakları — ROLE_ADMIN olmayan İK yetkilileri (ROLE_HR) için doğrudan giriş; yöneticiler /admin'den ulaşır */}
      <Restricted to="ROLE_HR">
        <MenuItem to="/admin/insan-kaynaklari" title="İnsan Kaynakları" />
      </Restricted>
      {/* ─── Tedarik ─────────────────────────────────────────────────
          Satın alma birimi. Her madde kendi yetkisiyle korunur; başlık
          yalnızca en az bir yetkisi olana görünür. Satışçının kendi talep
          sayfası burada DEĞİL — CRM > Tedarik Taleplerim altında.
          Mail günlüğü de burada değil — /admin sayfasında (yönetici aracı). */}
      {showPurchasingMenu && (
        <MenuInnerWithSub title="Tedarik" to="/purchasing" menuPlacement="bottom-start" menuTrigger="click">
          <Restricted to="ROLE_PURCHASING">
            <MenuItem
              to="/purchasing/requests"
              title="Tedarik Talepleri"
              icon="/media/icons/duotone/Communication/Mail-opened.svg"
            />
          </Restricted>
          <Restricted to="ROLE_PURCHASING">
            <MenuItem
              to="/purchasing/rfqs"
              title="Tedarikçi Sorguları"
              icon="/media/icons/duotone/Communication/Send.svg"
            />
          </Restricted>
          <Restricted to="ROLE_PURCHASING">
            <MenuItem
              to="/purchasing/suppliers"
              title="Tedarikçiler"
              icon="/media/icons/duotone/Home/Building.svg"
            />
          </Restricted>
        </MenuInnerWithSub>
      )}


      {/* <div className="position-relative d-lg-flex justify-content-center align-items-center">
        <div className="product-joyride">
          <MenuItem title="Ürünler" to="/products" />
        </div>
        <span className="bullet bullet-dot bg-success mt-2 me-2 w-8px h-8px position-absolute translate-middle top-25 end-0 animation-blink"></span>
      </div> */}

      {/* <MenuInnerWithSub title="Crafted" to="/crafted" menuPlacement="bottom-start" menuTrigger="click">
        <MenuInnerWithSub title="Pages" to="/crafted/pages" fontIcon="bi-archive" hasArrow={true} menuPlacement="right-start" menuTrigger={`{default:'click', lg: 'hover'}`}>
          <MenuInnerWithSub title="Profile" to="/crafted/pages/profile" hasArrow={true} hasBullet={true} menuPlacement="right-start" menuTrigger={`{default:'click', lg: 'hover'}`}>
            <MenuItem to="/crafted/pages/profile/overview" title="Overview" hasBullet={true} />
            <MenuItem to="/crafted/pages/profile/projects" title="Projects" hasBullet={true} />
            <MenuItem to="/crafted/pages/profile/campaigns" title="Campaigns" hasBullet={true} />
            <MenuItem to="/crafted/pages/profile/documents" title="Documents" hasBullet={true} />
            <MenuItem to="/crafted/pages/profile/connections" title="Connections" hasBullet={true} />
          </MenuInnerWithSub>
          <MenuInnerWithSub title="Wizards" to="/crafted/pages/wizards" hasArrow={true} hasBullet={true} menuPlacement="right-start" menuTrigger={`{default:'click', lg: 'hover'}`}>
            <MenuItem to="/crafted/pages/wizards/horizontal" title="Horizontal" hasBullet={true} />
            <MenuItem to="/crafted/pages/wizards/vertical" title="Vertical" hasBullet={true} />
          </MenuInnerWithSub>
        </MenuInnerWithSub>

        <MenuInnerWithSub title="Accounts" to="/crafted/accounts" fontIcon="bi-person" hasArrow={true} menuPlacement="right-start" menuTrigger={`{default:'click', lg: 'hover'}`}>
          <MenuItem to="/crafted/account/overview" title="Overview" hasBullet={true} />
          <MenuItem to="/crafted/account/settings" title="Settings" hasBullet={true} />
        </MenuInnerWithSub>

        <MenuInnerWithSub title="Errors" to="/error" fontIcon="bi-sticky" hasArrow={true} menuPlacement="right-start" menuTrigger={`{default:'click', lg: 'hover'}`}>
          <MenuItem to="/error/404" title="Error 404" hasBullet={true} />
          <MenuItem to="/error/500" title="Error 500" hasBullet={true} />
        </MenuInnerWithSub>

        <MenuInnerWithSub title="Widgets" to="/crafted/widgets" fontIcon="bi-layers" hasArrow={true} menuPlacement="right-start" menuTrigger={`{default:'click', lg: 'hover'}`}>
          <MenuItem to="/crafted/widgets/lists" title="Lists" hasBullet={true} />
          <MenuItem to="/crafted/widgets/statistics" title="Statistics" hasBullet={true} />
          <MenuItem to="/crafted/widgets/charts" title="Charts" hasBullet={true} />
          <MenuItem to="/crafted/widgets/mixed" title="Mixed" hasBullet={true} />
          <MenuItem to="/crafted/widgets/tables" title="Tables" hasBullet={true} />
          <MenuItem to="/crafted/widgets/feeds" title="Feeds" hasBullet={true} />
        </MenuInnerWithSub>
      </MenuInnerWithSub>

      <MenuInnerWithSub title="Apps" to="/apps" menuPlacement="bottom-start" menuTrigger="click">
        <MenuInnerWithSub
          title="Chat"
          to="/apps/chat"
          icon="/media/icons/duotone/Communication/Group-chat.svg"
          hasArrow={true}
          menuPlacement="right-start"
          menuTrigger={`{default:'click', lg: 'hover'}`}
        >
          <MenuItem to="/apps/chat/private-chat" title="Private Chat" hasBullet={true} />
          <MenuItem to="/apps/chat/group-chat" title="Group Chart" hasBullet={true} />
          <MenuItem to="/apps/chat/drawer-chat" title="Drawer Chart" hasBullet={true} />
        </MenuInnerWithSub>
      </MenuInnerWithSub>
      <MenuInnerWithSub isMega={true} title="Mega menu" to="/mega-menu" menuPlacement="bottom-start" menuTrigger="click">
        <MegaMenu />
      </MenuInnerWithSub> */}
    </>
  );
}
